import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import PassageChip from "../Passages/PassageChip";
import { DifficultyBadge } from "../Difficulty/Difficulty";
import { getQuestions } from "../../services/questionService";
import { getVariantCandidates } from "../../services/subjectVariantService";
import { getApiErrorCode } from "../../utils/apiError";
import { isCanceledError } from "../../utils/examError";
import { focusRing, linkButtonClass, Spinner, StatusBadge, useMediaQuery } from "../../pages/Exams/TestBuilder/ui";
import QuestionContent, { NumberBadge, QuestionLine } from "./QuestionContent";
import { dialogFooterClass, fieldClass, primaryButtonClass } from "./variantUi";
import { bankHref, isUnreadable, LEVELS, questionList, rowIdentity, rowKey, rowPlace, rowSubtopics, variantErrorMessage } from "./variantText";

const SEARCH_DELAY = 300;
const listOf = (data) => (Array.isArray(data) ? data : data?.results || []);
const LOADING = { loading: true, error: null, row: null, level: null, questions: [] };
// The place a target is for, kept when the dialog moves on to it after a read of the variant or a save of the matrix
// (a new target object, maybe a new row uuid): a question's place by its row, an empty one by its matrix row and level.
const placeOf = (target) => {
  if (!target) return null;
  return target.kind === "replace" ? `tq:${target.tq?.uuid}` : `${rowIdentity(target.row)}:${target.level}`;
};

// "In 2 other tests" / "In no other test": candidates in fewer tests come first.
function UsesLine({ uses }) {
  const [t] = useTranslation();
  return (
    <span className={uses ? "text-amber-700" : "text-emerald-700"}>
      {uses ? t("variants.candidates.uses", { count: uses }) : t("variants.candidates.unused")}
    </span>
  );
}

/**
 * Whether a passage fits the variant as it is (as add_passage decides): each of its questions takes a free place of
 * its row and difficulty, a question counting in the row whose subtopics have its subtopic, else in its topic's
 * whole-topic row. `rows` are the check's rows, `refs` the variant's questions. → {needs, short: [{entry, level,
 * count}], notReady (not approved, no difficulty or no row), present (in the variant already)}.
 */
function passageFit(questions, rows, refs) {
  const wants = new Map();
  const notReady = [];
  const present = [];
  questions.forEach((question) => {
    if (question.is_active === false) return;
    if (refs.has(question.uuid)) {
      present.push(question.uuid);
      return;
    }
    const subtopic = question.subtopic;
    const entry =
      subtopic &&
      (rows.find((item) => rowSubtopics(item.row).some((planned) => planned?.uuid === subtopic.uuid)) ||
        rows.find((item) => !!item.row?.topic && item.row.topic.uuid === subtopic.topic?.uuid && !rowSubtopics(item.row).length));
    if (question.status?.code !== "APPROVED" || !LEVELS.includes(question.difficulty) || !entry) {
      notReady.push(question.code || question.uuid);
      return;
    }
    const key = `${rowKey(entry.row)}:${question.difficulty}`;
    const want = wants.get(key) || { entry, level: question.difficulty, count: 0, order: rows.indexOf(entry) };
    want.count += 1;
    wants.set(key, want);
  });
  const needs = [...wants.values()].sort((a, b) => a.order - b.order || LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level));
  const short = needs
    .map((want) => ({ ...want, count: want.count - Math.max(0, (want.entry.planned?.[want.level] || 0) - (want.entry.actual?.[want.level] || 0)) }))
    .filter((want) => want.count > 0);
  return { needs, short, notReady, present };
}

// "A×1, B×1 (III тоқсан, 1-апта, Topic › Subtopic)": places by row.
function placesText(t, items) {
  const byRow = new Map();
  items.forEach(({ entry, level, count }) => {
    const key = rowKey(entry.row);
    const group = byRow.get(key) || { row: entry.row, levels: [] };
    group.levels.push(`${level}×${count}`);
    byRow.set(key, group);
  });
  return [...byRow.values()].map((group) => `${group.levels.join(", ")} (${rowPlace(t, group.row)})`).join("; ");
}

/**
 * The approved questions that may take a place of the variant (GET candidates/: those of the row and difficulty
 * that are not in it, the ones in fewer other tests first), with a search and each question's preview.
 * `target`: {kind: "add", row, level} for an empty place, or {kind: "replace", tq, row, level} for a question's
 * place (it keeps its number). A question of a passage only goes in with its whole passage (add only):
 * onChoosePassage(passage); otherwise onChoose(question). Before that, the passage's questions are looked up to say
 * which free places it takes and, when one is missing, why it can't go in (`rows`: the check's, `refs`: the
 * variant's questions). `refreshed`: the place was taken anew after the variant ("variant") or its matrix ("matrix")
 * changed meanwhile. A place no longer there (404) goes to `onGone(error)`; a row no longer in the matrix (400
 * row_not_found: it was saved meanwhile, and a row's uuid changes with every save) to `onStale(error)`, which reads
 * the variant again and moves the dialog on (the list waits meanwhile).
 */
export default function CandidatesDialog({
  isOpen,
  variantUuid,
  folderUuid,
  target,
  rows = [],
  refs,
  refreshed = null,
  onClose,
  onChoose,
  onChoosePassage,
  onGone,
  onStale,
}) {
  const [t] = useTranslation();
  const id = useId();
  const twoPanes = useMediaQuery("(min-width: 1024px)");
  const twoPanesRef = useRef(twoPanes);
  twoPanesRef.current = twoPanes;
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(LOADING);
  const [selected, setSelected] = useState(null);
  const membersRef = useRef(new Map()); // passage uuid → its questions, for this opening
  const [members, setMembers] = useState(null); // {passage, loading, error, questions} of the chosen passage

  // Opened, or on another place: nothing of the last one's search, list or choice stays. Set while rendering (not in
  // an effect), so the first request already goes without the last search; moved on to the same place, it keeps them.
  const place = placeOf(target);
  const [shown, setShown] = useState({ isOpen, place });
  if (shown.isOpen !== isOpen || shown.place !== place) {
    setShown({ isOpen, place });
    if (isOpen) {
      setSearch("");
      setQuery("");
      setSelected(null);
      setState(LOADING);
      setMembers(null);
    }
  }
  useEffect(() => {
    if (isOpen) membersRef.current = new Map();
  }, [isOpen, place]);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!isOpen || !target) return undefined;
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));
    // A question's place by the question; an empty one by its row (a pre-v2 server's rows have no uuid: by topic
    // and subtopic).
    let params;
    if (target.kind === "replace") params = { test_question: target.tq.uuid };
    else if (target.row?.uuid) params = { row: target.row.uuid, level: target.level };
    else params = { topic: target.row?.topic?.uuid, subtopic: target.row?.subtopic?.uuid || undefined, level: target.level };
    if (query) params.search = query;
    getVariantCandidates(variantUuid, { params, signal: controller.signal })
      .then((data) => {
        const questions = Array.isArray(data?.questions) ? data.questions : [];
        setState({ loading: false, error: null, row: data?.row || null, level: data?.level || null, questions });
        // Two panes show the first one; phones open one when it is tapped.
        setSelected((prev) => (questions.some((q) => q.uuid === prev) ? prev : twoPanesRef.current ? questions[0]?.uuid ?? null : null));
      })
      .catch((err) => {
        if (isCanceledError(err)) return;
        // The place is no longer there (its question was removed, the variant proposed anew or deleted), or its row
        // is no longer in the matrix (saved meanwhile): the page reads the variant again; a retry here would ask for
        // the same place forever.
        if (err?.response?.status === 404 && onGone) {
          onGone(err);
          return;
        }
        if (getApiErrorCode(err) === "row_not_found" && onStale) {
          onStale(err);
          return;
        }
        setState({ loading: false, error: err, row: null, level: null, questions: [] });
      });
    return () => controller.abort();
  }, [isOpen, target, query, variantUuid, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = state.questions.find((q) => q.uuid === selected) || null;
  // The chosen passage's questions, looked up once per passage while the dialog is open.
  const passageUuid = isOpen && target?.kind === "add" ? chosen?.passage?.uuid || null : null;
  useEffect(() => {
    if (!passageUuid) return undefined;
    const known = membersRef.current.get(passageUuid);
    if (known) {
      setMembers({ passage: passageUuid, questions: known });
      return undefined;
    }
    const controller = new AbortController();
    setMembers({ passage: passageUuid, loading: true });
    getQuestions({ params: { passage: passageUuid, ordering: "passage_order", page_size: 100 }, signal: controller.signal })
      .then((data) => {
        const questions = listOf(data);
        membersRef.current.set(passageUuid, questions);
        setMembers({ passage: passageUuid, questions });
      })
      .catch((err) => {
        if (!isCanceledError(err)) setMembers({ passage: passageUuid, error: err });
      });
    return () => controller.abort();
  }, [passageUuid]);
  const fit = useMemo(
    () => (members?.passage === passageUuid && members?.questions ? passageFit(members.questions, rows || [], refs || new Map()) : null),
    [members, passageUuid, rows, refs]
  );
  // A lookup that failed blocks nothing: the server decides.
  const passageBlocked = !!fit && (fit.short.length > 0 || fit.notReady.length > 0 || fit.present.length > 0);

  if (!target) return null;
  const row = state.row || target.row;
  const level = state.level || target.level;
  const isPassage = !!chosen?.passage?.uuid;
  // A question the user does not read is never offered (candidates are of the variant's own subject); if one came,
  // it would not be chosen unseen.
  const canChoose = isOpen && !!chosen && !isUnreadable(chosen) && (!isPassage || (target.kind === "add" && !passageBlocked));
  // The dialog fading out after a choice: a second click of a double click chooses nothing.
  const choose = (question) => {
    if (!isOpen) return;
    if (question.passage?.uuid) onChoosePassage(question.passage, question);
    else onChoose(question);
  };
  const title =
    target.kind === "replace"
      ? t("variants.candidates.replace_title", { number: target.tq.order })
      : t("variants.candidates.add_title");
  const reasonId = `${id}-passage-reason`;

  // What the chosen question would do: a passage's question goes in with its passage (only into an empty place),
  // which takes a free place for each of its questions.
  const passageNote = (question) => {
    if (!question?.passage?.uuid) return null;
    const own = members?.passage === question.passage.uuid;
    return (
      <div className="space-y-1.5 rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-sm text-indigo-900">
        <p className="flex items-start gap-2">
          <BookOpenIcon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
          {target.kind === "add"
            ? t("variants.candidates.passage_add_note", { name: question.passage.name || "" })
            : t("variants.candidates.passage_replace_note", { name: question.passage.name || "" })}
        </p>
        {target.kind === "add" && own && members.loading && (
          <p className="flex items-center gap-2 pl-6 text-xs text-indigo-800" role="status">
            <Spinner className="h-3.5 w-3.5" />
            {t("variants.candidates.passage_checking")}
          </p>
        )}
        {target.kind === "add" && own && fit && !passageBlocked && fit.needs.length > 0 && (
          <p className="pl-6 text-xs text-indigo-800 [overflow-wrap:anywhere]">{t("variants.candidates.passage_needs", { places: placesText(t, fit.needs) })}</p>
        )}
        {target.kind === "add" && own && passageBlocked && (
          <div id={reasonId} className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 [overflow-wrap:anywhere]">
            {fit.short.length > 0 && (
              <p className="flex items-start gap-1.5">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                {t("variants.candidates.passage_short", { places: placesText(t, fit.short) })}
              </p>
            )}
            {fit.notReady.length > 0 && (
              <p className="flex items-start gap-1.5">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                {t("variants.candidates.passage_not_ready", { list: fit.notReady.join(", ") })}
              </p>
            )}
            {fit.present.length > 0 && (
              <p className="flex items-start gap-1.5">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                {t("variants.candidates.passage_present", { list: questionList(t, fit.present, refs || new Map()) })}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };
  const chooseLabel = (question) =>
    question?.passage?.uuid ? t("variants.candidates.add_passage") : target.kind === "replace" ? t("variants.candidates.choose_replace") : t("variants.candidates.choose_add");

  const preview = (question) => (
    <div className="space-y-3">
      {passageNote(question)}
      <QuestionContent question={question} />
    </div>
  );

  let list;
  if (state.loading && !state.questions.length) {
    list = (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500" role="status">
        <Spinner />
        {t("variants.candidates.loading")}
      </div>
    );
  } else if (state.error) {
    // A page older than the server (400 stale_client) asks the old way again on a retry: only a reload helps.
    const stale = getApiErrorCode(state.error) === "stale_client";
    list = (
      <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {variantErrorMessage(t, state.error, { fallbackKey: "variants.candidates.load_failed" })}
        <button
          type="button"
          onClick={() => (stale ? window.location.reload() : setAttempt((n) => n + 1))}
          className={`${linkButtonClass} ml-auto max-sm:min-h-11`}
        >
          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
          {stale ? t("variants.reload_page") : t("test_bank.builder.retry")}
        </button>
      </div>
    );
  } else if (!state.questions.length) {
    const href = !query && row ? bankHref(folderUuid, row, level) : null;
    list = (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-10 text-center">
        <MagnifyingGlassIcon className="h-7 w-7 text-slate-300" aria-hidden="true" />
        <p className="max-w-md text-sm text-slate-600">
          {query ? t("variants.candidates.no_match", { search: query }) : t("variants.candidates.empty")}
        </p>
        {href && (
          <a href={href} target="_blank" rel="noopener noreferrer" className={`${linkButtonClass} max-sm:min-h-11`}>
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
            {t("variants.find_in_bank")}
          </a>
        )}
      </div>
    );
  } else {
    list = (
      <ul className={`space-y-2 ${state.loading ? "opacity-60" : ""}`} aria-label={t("variants.candidates.list_label")} aria-busy={state.loading}>
        {state.questions.map((question) => {
          const active = question.uuid === selected;
          return (
            <li key={question.uuid}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => setSelected(active && !twoPanes ? null : question.uuid)}
                className={`w-full rounded-xl border p-3 text-left transition duration-200 ${focusRing} ${
                  active ? "border-blue-600 bg-blue-50/60 ring-1 ring-blue-600" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60"
                }`}
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-xs font-medium text-slate-700">{question.code}</span>
                  <DifficultyBadge level={question.difficulty} />
                  <StatusBadge status={question.status} />
                  <PassageChip passage={question.passage} />
                </span>
                <QuestionLine question={question} className="mt-1 text-sm text-slate-700" />
                <span className="mt-1 block text-xs">
                  <UsesLine uses={question.uses || 0} />
                </span>
              </button>
              {!twoPanes && active && (
                <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3">
                  {preview(question)}
                  <button
                    type="button"
                    onClick={() => choose(question)}
                    disabled={!canChoose}
                    aria-describedby={isPassage && passageBlocked ? reasonId : undefined}
                    className={`${primaryButtonClass} mt-3 w-full`}
                  >
                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    {chooseLabel(question)}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <CustomDialog isOpen={isOpen} closeModal={onClose} headerText={title} maxWidth="max-w-6xl min-[1920px]:max-w-7xl">
      <div className="space-y-3">
        {refreshed && (
          <p role="status" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            {t(refreshed === "matrix" ? "variants.candidates.matrix_refreshed" : "variants.candidates.refreshed")}
          </p>
        )}
        <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <DifficultyBadge level={level} withLabel />
          <span className="min-w-0">{rowPlace(t, row)}</span>
        </p>
        {target.kind === "replace" && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm">
            <span className="text-slate-500">{t("variants.candidates.current")}</span>
            <NumberBadge number={target.tq.order} />
            <span className="font-mono text-xs text-slate-600">{target.tq.question?.code}</span>
            <QuestionLine question={target.tq.question} className="min-w-0 flex-1 text-slate-700" />
          </div>
        )}
        <div className="relative">
          <label htmlFor={`${id}-search`} className="sr-only">
            {t("variants.candidates.search_label")}
          </label>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            id={`${id}-search`}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("variants.candidates.search_placeholder")}
            className={`${fieldClass} h-10 pl-9`}
          />
          {state.loading && state.questions.length > 0 && <Spinner className="absolute right-3 top-3 h-4 w-4" />}
        </div>
        <p className="flex items-start gap-1.5 text-xs text-slate-500">
          <InformationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("variants.candidates.hint")}
        </p>
        <p className="sr-only" aria-live="polite">
          {state.loading || state.error ? "" : t("variants.candidates.count", { count: state.questions.length })}
        </p>
        {twoPanes && state.questions.length > 0 ? (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] min-[1920px]:grid-cols-[minmax(0,32rem)_minmax(0,1fr)]">
            <div className="max-h-[62vh] min-h-0 overflow-y-auto p-0.5">{list}</div>
            <div className="max-h-[62vh] min-h-0 overflow-y-auto rounded-xl border border-slate-200 p-4">
              {chosen ? preview(chosen) : <p className="py-10 text-center text-sm text-slate-500">{t("variants.candidates.pick_one")}</p>}
            </div>
          </div>
        ) : (
          list
        )}
      </div>
      <div className={dialogFooterClass}>
        <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={onClose} />
        {twoPanes && (
          <Button
            type="button"
            color="primary"
            onClick={() => chosen && choose(chosen)}
            disabled={!canChoose}
            aria-describedby={isPassage && passageBlocked ? reasonId : undefined}
          >
            <CheckIcon className="h-4 w-4" aria-hidden="true" />
            {chooseLabel(chosen)}
          </Button>
        )}
      </div>
    </CustomDialog>
  );
}
